import type { GoalNode, GoalPriority, GoalStatus } from '../api';

/** The canvas filters: goals that do not match are dimmed (never hidden — the tree stays readable). */
export interface GoalFilters {
  priorities: GoalPriority[];
  statuses: GoalStatus[];
  responsibleId: string | null;
  overdue: boolean;
}

export const EMPTY_FILTERS: GoalFilters = {
  priorities: [],
  statuses: [],
  responsibleId: null,
  overdue: false,
};

export const activeFilterCount = (filters: GoalFilters): number =>
  (filters.priorities.length > 0 ? 1 : 0) +
  (filters.statuses.length > 0 ? 1 : 0) +
  (filters.responsibleId ? 1 : 0) +
  (filters.overdue ? 1 : 0);

export function matchesFilters(goal: GoalNode, filters: GoalFilters): boolean {
  if (filters.priorities.length > 0 && !filters.priorities.includes(goal.priority)) return false;
  if (filters.statuses.length > 0 && !filters.statuses.includes(goal.status)) return false;
  if (filters.responsibleId && goal.responsible?.id !== filters.responsibleId) return false;
  if (filters.overdue && !goal.isOverdue) return false;
  return true;
}

const normalize = (text: string) => text.toLocaleLowerCase().replace(/ё/gu, 'е').trim();

/** Goals whose title contains every word of the query (case-insensitive, ё = е), in tree order. */
export function searchGoals(goals: readonly GoalNode[], query: string): string[] {
  const words = normalize(query).split(/\s+/u).filter(Boolean);
  if (words.length === 0) return [];
  return goals.filter((g) => words.every((w) => normalize(g.title).includes(w))).map((g) => g.id);
}

export type DeadlineState = 'overdue' | 'soon' | 'later' | 'none' | 'closed';

/** A day: what "near the deadline" means for the markers (amber clock). */
export const SOON_MS = 3 * 24 * 60 * 60 * 1000;

/** How a deadline looks: overdue (red), due within three days (amber), later, none, or closed (achieved/cancelled). */
export function deadlineState(
  goal: Pick<GoalNode, 'deadline' | 'status'>,
  now: number = Date.now(),
): DeadlineState {
  if (goal.status === 'achieved' || goal.status === 'cancelled') return goal.deadline ? 'closed' : 'none';
  if (!goal.deadline) return 'none';
  const at = new Date(goal.deadline).getTime();
  if (at < now) return 'overdue';
  return at - now <= SOON_MS ? 'soon' : 'later';
}
